/**
 * CC-024A: "Estimated" qualifier for lender-facing credit totals.
 *
 * Renders an italic "(Estimated)" label with an info icon and tooltip when
 * credit completeness data indicates the displayed total may be incomplete.
 *
 * Design mirrors CC-019 (member-side) implementation in FinancialOverview.tsx.
 * Single source of truth: relies solely on `show_estimated_qualifier` boolean
 * from the backend — does NOT derive visibility from multiple fields.
 */


import { Info } from 'lucide-react';
import { SimpleTooltip } from '../ui/Tooltip';
import type { LenderCreditCompleteness } from '../../services/lenderApiService';

/** Set of metric keys whose displayed values depend on credit account data */
export const CREDIT_DEPENDENT_METRICS = new Set([
  'total_outstanding_adverse_debt',
  'minimum_debt_payments',
  'credit_score',
  'dti_ratio',
  'net_savings',
  'money_leftover',
]);

interface EstimatedQualifierProps {
  /** Credit completeness payload from the backend (may be null/undefined for pre-framework members) */
  creditCompleteness: LenderCreditCompleteness | null | undefined;
  /**
   * When true, also renders the secondary disclosure line for partial/exception data.
   * Use in expanded detail views; omit in compact list rows.
   */
  showSecondaryDisclosure?: boolean;
}

/**
 * Inline qualifier rendered next to credit-derived totals.
 *
 * Usage:
 * ```tsx
 * <span className="text-2xl font-medium">{creditScore}</span>
 * <EstimatedQualifier creditCompleteness={member.credit_completeness} />
 * ```
 */
export default function EstimatedQualifier({
  creditCompleteness,
  showSecondaryDisclosure = false,
}: EstimatedQualifierProps) {
  // Graceful no-op when completeness data is absent (pre-framework member)
  if (!creditCompleteness?.show_estimated_qualifier) {
    return null;
  }

  const { submission_mode } = creditCompleteness;
  const showDisclosure =
    showSecondaryDisclosure &&
    (submission_mode === 'threshold_allowed' || submission_mode === 'exception_allowed');

  return (
    <span className="inline-flex flex-col">
      {/* Primary qualifier */}
      <SimpleTooltip
        content="This total may be incomplete because not all known credit accounts are connected and current."
        side="top"
      >
        <span
          className="inline-flex items-center gap-1 ml-1.5 text-slate-500 italic text-xs font-normal cursor-help"
          aria-label="Estimated value — credit data may be incomplete"
        >
          (Estimated)
          <Info className="w-3 h-3 flex-shrink-0" aria-hidden="true" />
        </span>
      </SimpleTooltip>

      {/* CC-024A: Secondary disclosure for partial/exception submission modes */}
      {showDisclosure && (
        <span className="text-[10px] text-amber-600 italic ml-1.5 mt-0.5 leading-tight">
          Based on partial or exception-resolved data.
        </span>
      )}
    </span>
  );
}

/**
 * Helper to check whether a given metric key is credit-dependent.
 * Used by MemberCard.tsx metrics list renderer.
 */
export function isCreditDependentMetric(key: string): boolean {
  return CREDIT_DEPENDENT_METRICS.has(key);
}
